import { slicedToArray as _slicedToArray } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useMemo, useContext, useState, useEffect, useRef } from 'react';
import { A11yMeaningfulLabelContext } from '../components/A11yMeaningfulLabel/A11yMeaningfulLabelPresentation.js';
import { handleKeydownListFlyout } from '../components/SelectFlyout/SelectFlyoutPresentation.js';
import { animationDuration } from '../styles/variables.js';
import { getViewportMediaQueryRange } from '../styles/viewport.js';
import { uniqueId } from './DomUtils.js';
import { Key } from './Keys.js';
import { getInitialViewportSize, addViewportCallback, removeViewportCallback } from './windowEvents/ResizeEvents.js';

function useUniqueId(prefix, fallbackId) {
  return useMemo(function () {
    return fallbackId || uniqueId(prefix);
  }, [prefix, fallbackId]);
}
function useMenu(menuItems) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpenButton = _useState2[1];
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    focussedIndex = _useState4[0],
    setFocussedIndex = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    scrollFocussedElementIntoView = _useState6[0],
    setScrollFocussedElementIntoView = _useState6[1];
  var _ref = [useRef(null), useRef(null), useRef(null), useRef(null)],
    buttonRef = _ref[0],
    containerRef = _ref[1],
    focussedRef = _ref[2],
    flyoutRef = _ref[3];
  var onFocussedValueChange = function onFocussedValueChange(newValue, newScroll) {
    setFocussedIndex(newValue);
    setScrollFocussedElementIntoView(newScroll);
  };
  var handleMouseOver = function handleMouseOver(value) {
    if (value !== focussedIndex) {
      onFocussedValueChange(value, false);
    }
  };
  var onKeyDown = function onKeyDown(event) {
    return handleKeydownListFlyout({
      event: event,
      open: open,
      focussedIndex: focussedIndex,
      options: menuItems.map(function (_ref2) {
        var label = _ref2.children;
        return {
          label: label
        };
      }),
      onChange: function onChange() {
        focussedRef.current.click();
      },
      onOpen: function onOpen() {
        setOpenButton(true);
        event.preventDefault();
      },
      onClose: function onClose() {
        return setOpenButton(false);
      },
      onFocusIndex: function onFocusIndex(value) {
        onFocussedValueChange(value, true);
      },
      flyoutElement: flyoutRef.current,
      focussedElement: focussedRef.current,
      additionalConfirmKeys: [Key.Space],
      useTypeAhead: true
    });
  };
  // TODO: duplicates SelectWrapper/AutocompleteWrapper
  var scrollFocussedValueIntoView = function scrollFocussedValueIntoView(focusTop) {
    var element = focussedRef.current;
    var host = flyoutRef.current;
    if (element) {
      if (focusTop) {
        host.scrollTop = Math.min(element.offsetTop, host.scrollHeight - host.offsetHeight);
        return;
      }
      // scroll flyout-list to selected item
      var elementAboveUpperEdge = element.offsetTop < host.scrollTop;
      var elementBelowLowerEdge = element.offsetTop + element.offsetHeight > host.scrollTop + host.offsetHeight;
      if (elementAboveUpperEdge) {
        // scroll element to the upper edge
        host.scrollTop = element.offsetTop;
      } else if (elementBelowLowerEdge) {
        // scroll element to the lower edge
        host.scrollTop = element.offsetTop + element.offsetHeight - host.offsetHeight;
      }
    }
  };
  useEffect(function () {
    scrollFocussedValueIntoView(scrollFocussedElementIntoView === "top");
  }, []);
  useEffect(function () {
    // TODO: without this timeout hack, somehow the focus gets overruled and the menu button is focussed.
    setTimeout(function () {
      var _a;
      return (_a = focussedRef === null || focussedRef === void 0 ? void 0 : focussedRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    });
  }, [focussedIndex, open]);
  return [{
    open: open,
    focussedIndex: focussedIndex
  }, {
    setOpen: setOpenButton,
    setFocussedIndex: setFocussedIndex
  }, {
    buttonRef: buttonRef,
    flyoutRef: flyoutRef,
    focussedRef: focussedRef,
    containerRef: containerRef
  }, {
    handleMouseOver: handleMouseOver,
    onKeyDown: onKeyDown
  }];
}
function useDwindle(actionProps) {
  var timeout = useRef(undefined);
  var minClickTimeExceeded = useRef(true);
  var mouseupPending = useRef(false);
  var hasKeyboardFocus;
  var setHasKeyboardFocus;
  var hasMouseHover;
  var setHasMouseHover;
  var clicked;
  var setClicked;
  if (actionProps.a11yMeaningfulLabel) {
    var onMouseDownNonInteractive;
    var onMouseUpNonInteractive;
    var _useContext = useContext(A11yMeaningfulLabelContext);
    hasKeyboardFocus = _useContext.hasKeyboardFocus;
    setHasKeyboardFocus = _useContext.onKeyboardFocusChange;
    hasMouseHover = _useContext.hasMouseHover;
    setHasMouseHover = _useContext.onMouseHoverChange;
    clicked = _useContext.isActive;
    onMouseDownNonInteractive = _useContext.onMouseDown;
    onMouseUpNonInteractive = _useContext.onMouseUp;
    setClicked = function setClicked(value) {
      return value ? onMouseDownNonInteractive() : onMouseUpNonInteractive();
    };
  } else if (actionProps.nonInteractive) {
    var _useContext2 = useContext(A11yMeaningfulLabelContext);
    hasKeyboardFocus = _useContext2.hasKeyboardFocus;
    hasMouseHover = _useContext2.hasMouseHover;
    clicked = _useContext2.isActive;
    setClicked = function setClicked() {};
    setHasMouseHover = function setHasMouseHover() {};
    setHasKeyboardFocus = function setHasKeyboardFocus() {};
  } else {
    var _useState7 = useState(false);
    var _useState8 = _slicedToArray(_useState7, 2);
    hasKeyboardFocus = _useState8[0];
    setHasKeyboardFocus = _useState8[1];
    var _useState9 = useState(false);
    var _useState10 = _slicedToArray(_useState9, 2);
    hasMouseHover = _useState10[0];
    setHasMouseHover = _useState10[1];
    var _useState11 = useState(false);
    var _useState12 = _slicedToArray(_useState11, 2);
    clicked = _useState12[0];
    setClicked = _useState12[1];
  }
  useEffect(function () {
    return function () {
      return window.clearTimeout(timeout.current);
    };
  }, []);
  function onMouseDown(ev) {
    if (actionProps.onMouseDown) {
      actionProps.onMouseDown(ev);
    }
    if (!actionProps.disabled && !clicked && !actionProps.nonInteractive) {
      mouseupPending.current = true;
      minClickTimeExceeded.current = false;
      if (timeout.current) {
        window.clearTimeout(timeout.current);
      }
      timeout.current = window.setTimeout(function () {
        minClickTimeExceeded.current = true;
        if (!mouseupPending.current) {
          setClicked(false);
          mouseupPending.current = false;
        }
      }, animationDuration.standard);
      setClicked(true);
    }
  }
  function onMouseUp(ev) {
    if (actionProps.onMouseUp) {
      actionProps.onMouseUp(ev);
    }
    if (!actionProps.disabled && !actionProps.nonInteractive) {
      mouseupPending.current = false;
      if (minClickTimeExceeded.current) {
        setClicked(false);
      }
    }
  }
  function onMouseLeave(ev) {
    if (actionProps.onMouseLeave) {
      actionProps.onMouseLeave(ev);
    }
    if (clicked && !actionProps.nonInteractive) {
      onMouseUp(ev);
    }
  }
  return [{
    hasKeyboardFocus: hasKeyboardFocus,
    hasMouseHover: hasMouseHover,
    clicked: clicked
  }, {
    setHasKeyboardFocus: setHasKeyboardFocus,
    setHasMouseHover: setHasMouseHover,
    setClicked: setClicked
  }, {
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp,
    onMouseLeave: onMouseLeave
  }];
}
// TODO use better type than IActionBaseProps
function useLinkHover(actionProps) {
  var hasKeyboardFocus;
  var setHasKeyboardFocus;
  var hasMouseHover;
  var setHasMouseHover;
  var onMouseDownContext = function onMouseDownContext() {};
  var onMouseUpContext = function onMouseUpContext() {};
  var onMouseLeaveContext = function onMouseLeaveContext() {};
  if (actionProps.a11yMeaningfulLabel) {
    var _useContext3 = useContext(A11yMeaningfulLabelContext);
    hasKeyboardFocus = _useContext3.hasKeyboardFocus;
    setHasKeyboardFocus = _useContext3.onKeyboardFocusChange;
    hasMouseHover = _useContext3.hasMouseHover;
    setHasMouseHover = _useContext3.onMouseHoverChange;
    onMouseDownContext = _useContext3.onMouseDown;
    onMouseUpContext = _useContext3.onMouseUp;
    onMouseLeaveContext = _useContext3.onMouseLeave;
  } else if (actionProps.nonInteractive) {
    var _useContext4 = useContext(A11yMeaningfulLabelContext);
    hasKeyboardFocus = _useContext4.hasKeyboardFocus;
    hasMouseHover = _useContext4.hasMouseHover;
    setHasMouseHover = function setHasMouseHover() {};
    setHasKeyboardFocus = function setHasKeyboardFocus() {};
  } else {
    var _useState13 = useState(false);
    var _useState14 = _slicedToArray(_useState13, 2);
    hasKeyboardFocus = _useState14[0];
    setHasKeyboardFocus = _useState14[1];
    var _useState15 = useState(false);
    var _useState16 = _slicedToArray(_useState15, 2);
    hasMouseHover = _useState16[0];
    setHasMouseHover = _useState16[1];
  }
  function onMouseDown(ev) {
    if (actionProps.onMouseDown) {
      actionProps.onMouseDown(ev);
    }
    onMouseDownContext();
  }
  function onMouseUp(ev) {
    if (actionProps.onMouseUp) {
      actionProps.onMouseUp(ev);
    }
    onMouseUpContext();
  }
  function onMouseLeave(ev) {
    if (actionProps.onMouseLeave) {
      actionProps.onMouseLeave(ev);
    }
    onMouseLeaveContext();
  }
  return [{
    hasKeyboardFocus: hasKeyboardFocus,
    hasMouseHover: hasMouseHover
  }, {
    setHasKeyboardFocus: setHasKeyboardFocus,
    setHasMouseHover: setHasMouseHover
  }, {
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp,
    onMouseLeave: onMouseLeave
  }];
}
function useViewportRange(min, max) {
  var _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    matches = _useState18[0],
    setMatches = _useState18[1];
  useEffect(function () {
    var mql = window.matchMedia(getViewportMediaQueryRange(min, max));
    var listener = function listener() {
      return setMatches(mql.matches);
    };
    listener();
    mql.addEventListener("change", listener);
    return function () {
      return mql.removeEventListener("change", listener);
    };
  }, [matches, min, max]);
  return matches;
}
function useViewport() {
  var _useState19 = useState(getInitialViewportSize()),
    _useState20 = _slicedToArray(_useState19, 2),
    size = _useState20[0],
    setSize = _useState20[1];
  useEffect(function () {
    addViewportCallback(setSize);
    return function () {
      removeViewportCallback(setSize);
    };
  }, []);
  return size;
}
function useResize(onResize) {
  useEffect(function () {
    window.addEventListener("resize", onResize);
    return function () {
      window.removeEventListener("resize", onResize);
    };
  }, []);
}

export { useDwindle, useLinkHover, useMenu, useResize, useUniqueId, useViewport, useViewportRange };
